import { create, protoInt64 } from "@bufbuild/protobuf";
import { LRUCache } from "lru-cache";
import stringify from "safe-stable-stringify";
import { MoneySchema, type Money } from "#gen/google/type/money_pb.js";
import type { PriceRange } from "#gen/products/v1/product_pb.js";

const moneyFormatters: LRUCache<string, Intl.NumberFormat> = new LRUCache({ max: 10, ttlAutopurge: true });

export function moneyAsNumber(money: Money): number {
	return Number(money.units) + Number(money.nanos) / Math.pow(10, 9);
}

function isFree(priceRange?: PriceRange): boolean {
	if (!priceRange) {
		return true;
	}
	return priceRange.minVariantPrice?.units == protoInt64.zero && priceRange.maxVariantPrice?.units == protoInt64.zero;
}

export function formatPriceRange(priceRange?: PriceRange, defaultCurrencyCode: string = "USD"): string {
	if (isFree(priceRange)) {
		return "Free";
	}
	if (equalPrice(priceRange?.minVariantPrice, priceRange?.maxVariantPrice)) {
		if (priceRange?.minVariantPrice) {
			return formatPrice(priceRange?.minVariantPrice, defaultCurrencyCode);
		} else {
			return formatPrice(priceRange?.maxVariantPrice, defaultCurrencyCode);
		}
	}
	let str: string = formatPrice(priceRange?.minVariantPrice, defaultCurrencyCode);
	if (priceRange?.maxVariantPrice) {
		str += ` - ${formatPrice(priceRange?.maxVariantPrice, defaultCurrencyCode)}`;
	}
	return str;
}

function equalPrice(a?: Money, b?: Money): boolean {
	return a?.units == b?.units && a?.currencyCode == b?.currencyCode;
}

export function formatPrice(price?: Money, defaultCurrencyCode: string = "USD", options?: Intl.NumberFormatOptions): string {
	if (!price) {
		return "";
	}
	const value = moneyAsNumber(price);
	return formatPriceFromNumber(value, price.currencyCode ?? defaultCurrencyCode, options);
}

export function formatPriceFromNumber(value: number, currencyCode: string = "USD", options?: Intl.NumberFormatOptions): string {
	if (!currencyCode) {
		currencyCode = "USD";
	}
	const key = options ? `${currencyCode}-${stringify(options)}` : currencyCode;
	let moneyFormatter = moneyFormatters.get(key);
	if (!moneyFormatter) {
		moneyFormatter = new Intl.NumberFormat("en-US", {
			...options,
			style: "currency",
			currency: currencyCode,
		});
		moneyFormatters.set(key, moneyFormatter);
	}
	return moneyFormatter.format(value);
}

export function formatCurrencyCompact(value: number, currencyCode: string = "USD", options: Intl.NumberFormatOptions | undefined = { notation: "compact", maximumFractionDigits: 2 }): string {
	return formatPriceFromNumber(value, currencyCode, options);
}

export function parseMoney(value: string, currencyCode: string = "USD"): Money | undefined {
	const money = create(MoneySchema);
	const parts = value.split(".");
	const units = parts[0].replaceAll(",", "").replaceAll("$", "");

	if (units.length > 0) {
		if (/[^0-9]/.test(units)) {
			return undefined;
		}

		try {
			money.units = BigInt(Number.parseFloat(units));
		} catch (_) {
			return undefined;
		}
	}

	if (parts.length > 1 && parts[1].length > 0) {
		if (/[^0-9]/.test(parts[1])) {
			return undefined;
		} else {
			money.nanos = Number.parseInt(parts[1] ?? "0") * Math.pow(10, 9);
		}
	}

	money.currencyCode = currencyCode;
	return money;
}
